/**
 * Live run state, reduced from the run event stream (prompt.md §48, §49, §77).
 *
 * WHY a reducer with an explicit cursor rather than "append whatever arrives":
 * a client may disconnect mid-run and reconnect hours later (prompt.md §49), and
 * the transcript it rebuilds must be exactly what a client that never
 * disconnected would have seen. So state is a pure function of
 * (events, cursor) — an event is applied at most once, and an event whose
 * sequence is not ahead of the cursor is ignored rather than merged.
 *
 * The durable transcript is a *separate* input. Streamed deltas are a view of a
 * run that has not been persisted yet; when the run completes, the persisted
 * message arrives and the streaming buffer is dropped.
 */

import { isLimitPause, type ApprovalId, type MessageId, type RunId, type RunPauseReason, type RunState, type ToolCallId } from "@dayvise/domain";
import type { ToolCallRecord } from "@dayvise/client-sdk";
import type { RunStreamEvent } from "@dayvise/event-protocol";

export interface LiveToolCall {
  readonly toolCallId: string;
  readonly toolId: string;
  readonly arguments: unknown;
  readonly status: "requested" | "running" | "completed" | "failed";
  readonly output: string;
  readonly result: unknown;
  readonly ok: boolean | null;
}

export interface LiveApproval {
  readonly approvalId: string;
  readonly toolId: string;
  readonly arguments: unknown;
  readonly riskLevel: string;
  readonly requestedAt: string;
}

export interface LiveClarification {
  readonly clarificationId: string;
  readonly questions: readonly unknown[];
  readonly requestedAt: string;
  /** Structured form declared by the clarify tool, when it asked for one. */
  readonly form?: unknown;
  readonly formRevision?: string | null;
}

export interface LiveTodoItem {
  readonly id: string;
  readonly content: string;
  readonly status: "pending" | "in_progress" | "completed" | "cancelled";
  readonly parent?: string;
}

export interface LiveTodoSummary {
  readonly total: number;
  readonly pending: number;
  readonly in_progress: number;
  readonly completed: number;
  readonly cancelled: number;
}

export interface LivePatchedFile {
  readonly path?: string;
  readonly filesModified: readonly string[];
  readonly filesCreated: readonly string[];
  readonly filesDeleted: readonly string[];
  readonly diff: string;
}

export interface LiveProcessPrompt {
  readonly processId: string;
  readonly prompt: string;
  readonly requestedAt: string;
}

export interface LiveRunState {
  readonly runId: RunId | null;
  /** Highest sequence applied. Pass this back as `afterSequence` on reconnect. */
  readonly cursor: number;
  /** Event ids already applied, so a replay cannot double-apply a delta. */
  readonly seenEventIds: readonly string[];
  readonly state: RunState | null;
  readonly pauseReason: RunPauseReason | null;
  /** Accumulated assistant text for the in-flight message, if any. */
  readonly streamingText: string;
  readonly pendingMessages: readonly { messageId: string; text: string }[];
  /** Set once the run has produced a committed message; the buffer is then cleared. */
  readonly lastCompletedMessageId: MessageId | null;
  readonly toolCalls: readonly LiveToolCall[];
  readonly pendingApprovals: readonly LiveApproval[];
  readonly pendingClarifications: readonly LiveClarification[];
  readonly pendingProcessPrompts: readonly LiveProcessPrompt[];
  readonly todos: readonly LiveTodoItem[];
  readonly todoRevision: number;
  readonly todoSummary: LiveTodoSummary | null;
  readonly lastPatch: LivePatchedFile | null;
  readonly error: { readonly errorClass: string; readonly message: string; readonly retryable: boolean } | null;
  /** True when the buffer no longer covers the client's cursor (prompt.md §49). */
  readonly snapshotRequired: boolean;
  readonly usage: { readonly inputTokens: number; readonly outputTokens: number; readonly toolCalls: number };
}

export const EMPTY_LIVE_RUN: LiveRunState = {
  runId: null,
  cursor: -1,
  seenEventIds: [],
  state: null,
  pauseReason: null,
  streamingText: "",
  pendingMessages: [],
  lastCompletedMessageId: null,
  toolCalls: [],
  pendingApprovals: [],
  pendingClarifications: [],
  pendingProcessPrompts: [],
  todos: [],
  todoRevision: 0,
  todoSummary: null,
  lastPatch: null,
  error: null,
  snapshotRequired: false,
  usage: { inputTokens: 0, outputTokens: 0, toolCalls: 0 },
};

/** Bound on the duplicate-suppression index, mirroring the transport. */
export const MAX_SEEN_EVENT_IDS = 5_000;

export function beginRun(runId: RunId, previous: LiveRunState = EMPTY_LIVE_RUN): LiveRunState {
  // A different run resets the buffer but keeps nothing else: deltas from the
  // previous run must never appear in the new transcript.
  if (previous.runId === runId) return previous;
  return { ...EMPTY_LIVE_RUN, runId };
}

export function markSnapshotRequired(state: LiveRunState, latestSequence: number): LiveRunState {
  return { ...state, snapshotRequired: true, cursor: Math.max(state.cursor, latestSequence) };
}

/**
 * Apply one stream event. Pure: the same events in the same order always produce
 * the same state, which is what makes reconnect behaviour testable.
 */
export function applyRunEvent(state: LiveRunState, event: RunStreamEvent): LiveRunState {
  if (state.seenEventIds.includes(event.eventId)) return state;
  if (event.sequence <= state.cursor) return state;

  const seen = [...state.seenEventIds, event.eventId].slice(-MAX_SEEN_EVENT_IDS);
  const base: LiveRunState = {
    ...state,
    runId: event.runId,
    cursor: event.sequence,
    seenEventIds: seen,
    snapshotRequired: false,
  };

  const payload = event.payload as Record<string, unknown>;

  switch (event.type) {
    case "run.created":
      return { ...base, state: base.state ?? "queued" };
    case "run.queued":
      return { ...base, state: "queued" };
    case "run.started":
      return { ...base, state: "running", error: null };
    case "assistant.output.delta":
      return { ...base, state: "running", streamingText: base.streamingText + String(payload.delta ?? "") };
    case "assistant.message.completed":
      // The committed message is now durable, so the streaming buffer is spent.
      return {
        ...base, streamingText: "", lastCompletedMessageId: (payload.messageId as MessageId | null) ?? null,
        pendingMessages: typeof payload.messageId === "string"
          ? [...base.pendingMessages, { messageId: payload.messageId, text: String(payload.content ?? payload.text ?? base.streamingText) }]
          : base.pendingMessages,
      };
    case "tool.requested":
      return { ...base, state: "waiting_tool", toolCalls: upsertToolCall(base.toolCalls, event, "requested") };
    case "tool.started":
      return { ...base, state: "waiting_tool", toolCalls: upsertToolCall(base.toolCalls, event, "running") };
    case "tool.output": {
      const toolCallId = String(payload.toolCallId ?? "");
      return {
        ...base,
        toolCalls: base.toolCalls.map((call) =>
          call.toolCallId === toolCallId ? { ...call, output: call.output + String(payload.chunk ?? "") } : call,
        ),
      };
    }
    case "tool.completed": {
      const toolCallId = String(payload.toolCallId ?? "");
      const ok = payload.ok === true;
      return {
        ...base,
        state: "running",
        toolCalls: base.toolCalls.map((call) =>
          call.toolCallId === toolCallId ? { ...call, status: ok ? "completed" : "failed", ok, result: payload.result } : call,
        ),
        usage: { ...base.usage, toolCalls: base.usage.toolCalls + 1 },
      };
    }
    case "run.waiting_approval": {
      const approval: LiveApproval = {
        approvalId: String(payload.approvalId ?? ""),
        toolId: String(payload.toolId ?? ""),
        arguments: payload.arguments,
        riskLevel: String(payload.riskLevel ?? "medium"),
        requestedAt: event.timestamp,
      };
      const withoutDuplicate = base.pendingApprovals.filter((entry) => entry.approvalId !== approval.approvalId);
      return { ...base, state: "waiting_approval", pendingApprovals: [...withoutDuplicate, approval] };
    }
    case "approval.received": {
      const approvalId = String(payload.approvalId ?? "");
      return {
        ...base,
        state: "running",
        pendingApprovals: base.pendingApprovals.filter((entry) => entry.approvalId !== approvalId),
      };
    }
    case "run.waiting_clarification": {
      const clarification: LiveClarification = {
        clarificationId: String(payload.clarificationId ?? ""),
        questions: Array.isArray(payload.questions) ? payload.questions : [],
        requestedAt: event.timestamp,
      };
      const withoutDuplicate = base.pendingClarifications.filter(
        (entry) => entry.clarificationId !== clarification.clarificationId,
      );
      return { ...base, state: "waiting_approval", pendingClarifications: [...withoutDuplicate, clarification] };
    }
    case "clarification.received": {
      const clarificationId = String(payload.clarificationId ?? "");
      return {
        ...base,
        state: "running",
        pendingClarifications: base.pendingClarifications.filter((entry) => entry.clarificationId !== clarificationId),
      };
    }
    case "run.steered":
      // Steering is a control event and does not touch the transcript; recording
      // it here would create a message the server never wrote.
      return base;
    case "run.paused": {
      const reason = (payload.reason as RunPauseReason | undefined) ?? null;
      const explicitState = payload.state as RunState | undefined;
      const state: RunState =
        explicitState === "paused_user" || explicitState === "paused_limit"
          ? explicitState
          : (reason && isLimitPause(reason) ? "paused_limit" : "paused_user");
      return {
        ...base,
        state,
        pauseReason: reason,
      };
    }
    case "run.resumed":
      return { ...base, state: "running", pauseReason: null };
    case "run.usage":
      return {
        ...base,
        usage: {
          inputTokens: Number(payload.inputTokens ?? base.usage.inputTokens),
          outputTokens: Number(payload.outputTokens ?? base.usage.outputTokens),
          toolCalls: Number(payload.toolCalls ?? base.usage.toolCalls),
        },
      };
    case "run.error":
      return {
        ...base,
        error: {
          errorClass: String(payload.errorClass ?? "unknown"),
          message: String(payload.message ?? ""),
          retryable: payload.retryable === true,
        },
        state: payload.retryable === true ? "failed_retryable" : "failed_terminal",
      };
    case "run.todo_updated":
      return {
        ...base,
        todos: Array.isArray(payload.todos) ? (payload.todos as readonly LiveTodoItem[]) : base.todos,
        todoRevision: typeof payload.revision === "number" ? payload.revision : base.todoRevision,
        todoSummary: (payload.summary as LiveTodoSummary) ?? base.todoSummary,
      };
    case "file.patched":
      return {
        ...base,
        lastPatch: {
          path: typeof payload.path === "string" ? payload.path : undefined,
          filesModified: Array.isArray(payload.filesModified) ? (payload.filesModified as readonly string[]) : [],
          filesCreated: Array.isArray(payload.filesCreated) ? (payload.filesCreated as readonly string[]) : [],
          filesDeleted: Array.isArray(payload.filesDeleted) ? (payload.filesDeleted as readonly string[]) : [],
          diff: typeof payload.diff === "string" ? payload.diff : "",
        },
      };
    case "process.prompt": {
      const prompt: LiveProcessPrompt = {
        processId: String(payload.processId ?? ""),
        prompt: String(payload.prompt ?? ""),
        requestedAt: event.timestamp,
      };
      const withoutDup = base.pendingProcessPrompts.filter((p) => p.processId !== prompt.processId);
      return {
        ...base,
        pendingProcessPrompts: [...withoutDup, prompt],
      };
    }
    case "process.output":
      return base;
    case "run.completed":
      return {
        ...base,
        state: (typeof payload.state === "string" ? (payload.state as RunState) : "completed"),
        streamingText: "",
      };
    default:
      return base;
  }
}

export function dismissProcessPrompt(state: LiveRunState, processId: string): LiveRunState {
  return {
    ...state,
    pendingProcessPrompts: state.pendingProcessPrompts.filter((p) => p.processId !== processId),
  };
}

function upsertToolCall(toolCalls: readonly LiveToolCall[], event: RunStreamEvent, status: LiveToolCall["status"]): readonly LiveToolCall[] {
  const payload = event.payload as Record<string, unknown>;
  const toolCallId = String(payload.toolCallId ?? "");
  const existing = toolCalls.find((call) => call.toolCallId === toolCallId);
  if (existing) return toolCalls.map((call) => (call.toolCallId === toolCallId ? { ...call, status } : call));
  return [
    ...toolCalls,
    {
      toolCallId,
      toolId: String(payload.toolId ?? ""),
      arguments: payload.arguments,
      status,
      output: "",
      result: undefined,
      ok: null,
    },
  ];
}

export function applyRunEvents(state: LiveRunState, events: readonly RunStreamEvent[]): LiveRunState {
  return events.reduce(applyRunEvent, state);
}

/**
 * Reconcile a live buffer with the durable transcript.
 *
 * Called after a reconnect or a refetch: once the server has the committed
 * message, the streamed buffer for it must be discarded rather than shown twice.
 */
export function reconcileWithTranscript(state: LiveRunState, transcriptMessageIds: readonly string[]): LiveRunState {
  const pendingMessages = state.pendingMessages.filter((message) => !transcriptMessageIds.includes(message.messageId));
  return pendingMessages.length === state.pendingMessages.length ? state : { ...state, pendingMessages };
}

/** A tool call is "in flight" while the run is waiting on it. */
export function inFlightToolCalls(state: LiveRunState): readonly LiveToolCall[] {
  return state.toolCalls.filter((call) => call.status === "requested" || call.status === "running");
}

export function isRunSettled(state: LiveRunState): boolean {
  return state.state === "completed" || state.state === "failed_terminal" || state.state === "cancelled";
}

/** Merge recorded tool calls from `GET /v1/runs/:id` into the live view. */
export function mergeRecordedToolCalls(state: LiveRunState, recorded: readonly ToolCallRecord[]): LiveRunState {
  const merged = [...state.toolCalls];
  for (const record of recorded) {
    if (record.toolCallId === null) continue;
    const index = merged.findIndex((call) => call.toolCallId === record.toolCallId);
    const next: LiveToolCall = {
      toolCallId: record.toolCallId,
      toolId: record.toolId,
      arguments: record.arguments,
      status: record.status === "completed" ? "completed" : record.status === "failed" ? "failed" : "running",
      output: merged[index]?.output ?? "",
      result: record.result,
      ok: record.status === "completed" ? true : record.status === "failed" ? false : null,
    };
    if (index === -1) merged.push(next);
    else merged[index] = next;
  }
  return { ...state, toolCalls: merged };
}

/**
 * Rebuild pending clarifications from the durable tool-call records.
 *
 * A clarification requested while the client was offline leaves no trace in
 * the stream buffer the reconnect replays, so the prompt would never render.
 * The `clarify` tool call IS the durable record of it: the gateway marks the
 * row `awaiting_approval` and stores the questions in its arguments. Any row
 * still awaiting an answer is a prompt the user must see; a row the gateway
 * has since completed was answered elsewhere and must not resurrect here.
 *
 * The clarification id is the tool call id (the runtime's answer matcher
 * accepts it), which is what makes a recovered prompt answerable at all.
 */
export function mergeRecordedClarifications(state: LiveRunState, recorded: readonly ToolCallRecord[]): LiveRunState {
  const recovered = new Map<string, LiveClarification>();
  for (const record of recorded) {
    if (record.toolId !== "clarify" || record.toolCallId === null) continue;
    if (record.status === "awaiting_approval") {
      const args = record.arguments as { questions?: unknown[]; form?: unknown } | null;
      const questions = Array.isArray(args?.questions) ? args.questions : [];
      recovered.set(record.toolCallId, {
        clarificationId: record.toolCallId,
        questions,
        requestedAt: record.createdAt ?? "",
        ...(args?.form === undefined ? {} : { form: args.form }),
      });
    }
  }
  const live = state.pendingClarifications.filter((entry) => {
    const record = recovered.get(entry.clarificationId);
    return record !== undefined || !recorded.some(
      (r) => r.toolId === "clarify" && r.toolCallId === entry.clarificationId,
    );
  });
  const known = new Set(live.map((entry) => entry.clarificationId));
  const added = [...recovered.values()].filter((entry) => !known.has(entry.clarificationId));
  return { ...state, pendingClarifications: [...live, ...added] };
}

export type { ApprovalId, ToolCallId };
